import React, { useEffect, useRef, useState } from 'react';
import { ArrowsFullscreen, FullscreenExit, BoxArrowUpRight, DashLg, X } from 'react-bootstrap-icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { DASHBOARD_ROUTES } from '../../constants/dashboardRoutes';
import { APP_SHELL_HEADER_HEIGHT } from '../../constants/appShellLayout';
import {
  MOBILE_AI_NUB_BOTTOM,
  MOBILE_AI_NUB_RIGHT,
  MOBILE_AI_NUB_SIZE,
} from '../../constants/mobileAiNub';
import PrecisionAIIcon from '../shared/PrecisionAIIcon';
import AIChatPanel from './AIChatPanel';

const COMPACT_MIN_WIDTH = 360;
const COMPACT_WIDTH = 460;
const COMPACT_MIN_HEIGHT = 500;
const COMPACT_HEIGHT = 660;
const EXPANDED_MIN_WIDTH = 560;
const EXPANDED_WIDTH = 760;
const FLOATING_GUTTER = 20;
const COLLAPSED_HORIZONTAL_INSET = 12;
const COLLAPSED_VERTICAL_INSET = 40;
const FLOATING_POSITION_KEY = 'precisionai.floatingPosition';
const DRAG_THRESHOLD_PX = 4;

function readFloatingPosition() {
  try {
    const raw = localStorage.getItem(FLOATING_POSITION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Number.isFinite(parsed?.left) || !Number.isFinite(parsed?.top)) return null;
    return { left: parsed.left, top: parsed.top };
  } catch {
    return null;
  }
}

function writeFloatingPosition(position) {
  try {
    localStorage.setItem(FLOATING_POSITION_KEY, JSON.stringify(position));
  } catch {
    // Private mode can reject localStorage; the in-memory position still works.
  }
}

export function clampFloatingChatPosition(left, top, width, height, sidebarWidth = 0) {
  const minLeft = FLOATING_GUTTER;
  const minTop = APP_SHELL_HEADER_HEIGHT + 8;
  const maxLeft = Math.max(minLeft, window.innerWidth - sidebarWidth - width - FLOATING_GUTTER);
  const maxTop = Math.max(minTop, window.innerHeight - height - FLOATING_GUTTER);
  return {
    left: Math.min(Math.max(minLeft, left), maxLeft),
    top: Math.min(Math.max(minTop, top), maxTop),
  };
}
/**
 * One persistent PrecisionAI instance that moves between:
 * - the full dashboard PrecisionAI page
 * - a compact/expanded chatbot that can be dragged on desktop and closed with X
 * - a full-screen mobile overlay (Trade) that minimizes without leaving the page
 *
 * Keeping a single mounted AIChatPanel preserves the active chat while navigating.
 *
 * @param {number} sidebarWidth - Desktop dashboard sidebar offset
 * @param {boolean} mobileOverlay - When true, open via `precisionAI:open` as a full-screen
 *   overlay with minimize.
 * @param {boolean} [showMobileCollapsedNub=false] - Show the edge nub while overlay is minimized
 *   (e.g. Trade chart fullscreen when BottomNavTray is hidden).
 * @param {boolean} [showDesktopCollapsedNub=true] - Show the floating circle while the desktop
 *   chatbot is closed. Trade header hosts this control, so Trade passes false.
 */
export default function FloatingPrecisionAIChat({
  sidebarWidth = 0,
  mobileOverlay = false,
  showMobileCollapsedNub = false,
  showDesktopCollapsedNub = true,
}) {
  const { colors } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const pageMode = location.pathname === DASHBOARD_ROUTES.ai
    || location.pathname.startsWith(`${DASHBOARD_ROUTES.ai}/`);
  const journalsPage = location.pathname === DASHBOARD_ROUTES.journals
    || location.pathname.startsWith(`${DASHBOARD_ROUTES.journals}/`);
  const hideForAutomations = location.pathname === DASHBOARD_ROUTES.aiAutomations
    || location.pathname.startsWith(`${DASHBOARD_ROUTES.aiAutomations}/`);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [position, setPosition] = useState(readFloatingPosition);
  const [dragging, setDragging] = useState(false);
  const panelRef = useRef(null);
  const dragRef = useRef(null);
  const sidebarWidthRef = useRef(sidebarWidth);
  sidebarWidthRef.current = sidebarWidth;

  useEffect(() => {
    const onOpen = () => setOpen(true);
    const onClose = () => setOpen(false);
    window.addEventListener('precisionAI:open', onOpen);
    window.addEventListener('precisionAI:close', onClose);
    return () => {
      window.removeEventListener('precisionAI:open', onOpen);
      window.removeEventListener('precisionAI:close', onClose);
    };
  }, []);

  useEffect(() => {
    if (pageMode || mobileOverlay) return undefined;
    const clampCurrent = () => {
      setPosition((current) => {
        if (!current) return current;
        const panel = panelRef.current;
        const width = panel?.offsetWidth || (expanded ? EXPANDED_WIDTH : COMPACT_WIDTH);
        const height = panel?.offsetHeight || (expanded ? window.innerHeight : COMPACT_HEIGHT);
        const next = clampFloatingChatPosition(current.left, current.top, width, height, sidebarWidthRef.current);
        if (next.left === current.left && next.top === current.top) return current;
        writeFloatingPosition(next);
        return next;
      });
    };
    clampCurrent();
    window.addEventListener('resize', clampCurrent);
    return () => window.removeEventListener('resize', clampCurrent);
  }, [sidebarWidth, expanded, open, pageMode, mobileOverlay]);

  const onHeaderPointerDown = (event) => {
    if (pageMode || mobileOverlay || event.button !== 0) return;
    if (event.target instanceof Element && event.target.closest('button')) return;
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    dragRef.current = {
      pointerId: event.pointerId,
      originX: event.clientX,
      originY: event.clientY,
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
      moved: false,
      last: null,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const onHeaderPointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const dx = event.clientX - drag.originX;
    const dy = event.clientY - drag.originY;
    if (!drag.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
    drag.moved = true;
    const next = clampFloatingChatPosition(
      drag.left + dx,
      drag.top + dy,
      drag.width,
      drag.height,
      sidebarWidthRef.current,
    );
    drag.last = next;
    setDragging(true);
    setPosition(next);
  };

  const endDrag = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    setDragging(false);
    if (drag.moved && drag.last) writeFloatingPosition(drag.last);
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const right = sidebarWidth + FLOATING_GUTTER;
  const panelVisible = !hideForAutomations && (pageMode || open);
  const placed = !pageMode && !mobileOverlay && position;
  const topOffset = placed ? position.top : APP_SHELL_HEADER_HEIGHT + FLOATING_GUTTER;
  const widthBudget = placed
    ? `calc(100vw - ${position.left + FLOATING_GUTTER}px)`
    : `calc(100vw - ${right + FLOATING_GUTTER}px)`;
  const heightBudget = `calc(100dvh - ${topOffset + FLOATING_GUTTER}px)`;

  const panelStyle = pageMode
    ? {
        position: 'fixed',
        top: APP_SHELL_HEADER_HEIGHT,
        left: 0,
        right: sidebarWidth,
        bottom: 0,
        zIndex: 20,
        display: 'flex',
        flexDirection: 'column',
        background: colors.bgPrimary,
      }
    : mobileOverlay
      ? {
          position: 'fixed',
          inset: 0,
          zIndex: 1200,
          display: 'flex',
          flexDirection: 'column',
          background: colors.bgPrimary,
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }
      : {
          position: 'fixed',
          ...(placed
            ? { left: position.left, top: position.top }
            : { right, top: APP_SHELL_HEADER_HEIGHT + FLOATING_GUTTER }),
          width: expanded
            ? `min(clamp(${EXPANDED_MIN_WIDTH}px, 60vw, ${EXPANDED_WIDTH}px), ${widthBudget})`
            : `min(clamp(${COMPACT_MIN_WIDTH}px, 34vw, ${COMPACT_WIDTH}px), ${widthBudget})`,
          height: expanded
            ? heightBudget
            : `min(clamp(${COMPACT_MIN_HEIGHT}px, 64dvh, ${COMPACT_HEIGHT}px), ${heightBudget})`,
          minHeight: 0,
          zIndex: 1100,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 14,
          border: `1px solid ${colors.borderLight || 'rgba(255,255,255,0.14)'}`,
          background: colors.bgPrimary,
          boxShadow: `0 12px 30px rgba(0, 0, 0, 0.24), 0 0 0 1px ${colors.primary}26, 0 0 24px -4px ${colors.primary}40`,
        };

  const iconButtonStyle = {
    width: 30,
    height: 30,
    padding: 0,
    border: 'none',
    borderRadius: 7,
    background: 'transparent',
    color: colors.textMuted,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const showChrome = !pageMode && open && !hideForAutomations;
  const desktopFloating = showChrome && !mobileOverlay;
  const showCollapsedNub = !hideForAutomations && !pageMode && !open && (
    mobileOverlay ? showMobileCollapsedNub : showDesktopCollapsedNub
  );

  return (
    <>
      <div ref={panelRef} style={{ ...panelStyle, display: panelVisible ? 'flex' : 'none' }}>
        {showChrome && (
          <div
            onPointerDown={desktopFloating ? onHeaderPointerDown : undefined}
            onPointerMove={desktopFloating ? onHeaderPointerMove : undefined}
            onPointerUp={desktopFloating ? endDrag : undefined}
            onPointerCancel={desktopFloating ? endDrag : undefined}
            title={desktopFloating ? 'Drag to move' : undefined}
            style={{
              height: 46,
              padding: '0 8px 0 12px',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              borderBottom: `1px solid ${colors.borderLight}`,
              background: colors.bgSecondary,
              cursor: desktopFloating ? (dragging ? 'grabbing' : 'grab') : 'default',
              userSelect: 'none',
              touchAction: desktopFloating ? 'none' : undefined,
            }}
          >
            <PrecisionAIIcon size={20} active />
            <span style={{ color: colors.text, fontWeight: 650, fontSize: 14, flex: 1 }}>
              PrecisionAI
            </span>
            {!mobileOverlay && (
              <>
                <button
                  type="button"
                  onClick={() => navigate(DASHBOARD_ROUTES.ai)}
                  title="Open PrecisionAI page"
                  aria-label="Open PrecisionAI page"
                  style={iconButtonStyle}
                >
                  <BoxArrowUpRight size={15} />
                </button>
                <button
                  type="button"
                  onClick={() => setExpanded((value) => !value)}
                  title={expanded ? 'Restore chatbot size' : 'Expand chatbot'}
                  aria-label={expanded ? 'Restore chatbot size' : 'Expand chatbot'}
                  style={iconButtonStyle}
                >
                  {expanded ? <FullscreenExit size={15} /> : <ArrowsFullscreen size={15} />}
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                try { window.dispatchEvent(new CustomEvent('precisionAI:close')); } catch (_) {}
              }}
              title={mobileOverlay ? 'Minimize PrecisionAI' : 'Close PrecisionAI'}
              aria-label={mobileOverlay ? 'Minimize PrecisionAI' : 'Close PrecisionAI'}
              style={iconButtonStyle}
            >
              {mobileOverlay ? <DashLg size={18} /> : <X size={16} />}
            </button>
          </div>
        )}
        <AIChatPanel pageLayout={pageMode || mobileOverlay} />
      </div>

      {showCollapsedNub && (
        mobileOverlay || showMobileCollapsedNub ? (
          <button
            type="button"
            onClick={() => setOpen(true)}
            title="Open PrecisionAI"
            aria-label="Open PrecisionAI chatbot"
            style={{
              position: 'fixed',
              right: MOBILE_AI_NUB_RIGHT,
              bottom: MOBILE_AI_NUB_BOTTOM,
              top: 'auto',
              left: 'auto',
              zIndex: 1100,
              width: MOBILE_AI_NUB_SIZE,
              height: MOBILE_AI_NUB_SIZE,
              padding: 0,
              border: `1px solid ${colors.primary}`,
              borderRadius: '50%',
              background: colors.primary,
              color: '#fff',
              boxShadow: '0 4px 14px rgba(0,0,0,0.28)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              cursor: 'pointer',
            }}
          >
            <PrecisionAIIcon size={22} color="#fff" />
            <span style={{ fontSize: 9, fontWeight: 800, lineHeight: 1 }}>AI</span>
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setOpen(true)}
            title="Open PrecisionAI"
            aria-label="Open PrecisionAI chatbot"
            style={{
              position: 'fixed',
              right: right + COLLAPSED_HORIZONTAL_INSET,
              ...(journalsPage
                ? { top: 'auto', bottom: FLOATING_GUTTER }
                : { top: APP_SHELL_HEADER_HEIGHT + COLLAPSED_VERTICAL_INSET }),
              zIndex: 1100,
              width: 'clamp(54px, 4.3vw, 62px)',
              height: 'clamp(54px, 4.3vw, 62px)',
              borderRadius: '50%',
              border: `1px solid ${colors.primary}`,
              background: colors.primary,
              color: '#fff',
              boxShadow: '0 5px 16px rgba(0, 0, 0, 0.24)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              cursor: 'pointer',
            }}
          >
            <PrecisionAIIcon size={26} color="#fff" />
            <span style={{ fontSize: 10, fontWeight: 800, lineHeight: 1 }}>AI</span>
          </button>
        )
      )}
    </>
  );
}
